import { useQuery } from "@tanstack/react-query";
import type { CSSProperties } from "react";
import { Coin } from "../components/ui/Coin.js";
import { Icon } from "../components/ui/Icon.js";
import { fetchPortfolio, type PortfolioHolding } from "../lib/api.js";

const COIN_COLORS: Record<string, string> = {
  BTC: "#F7931A",
  ETH: "#627EEA",
  SOL: "#9945FF",
  BNB: "#F3BA2F",
  USDT: "#26A17B",
  USDC: "#2775CA",
  ADA: "#0033AD",
  AVAX: "#E84142",
  MATIC: "#8247E5",
  DOT: "#E6007A",
  LINK: "#2A5ADA",
  ARB: "#28A0F0",
  XRP: "#23292F",
  DOGE: "#C2A633",
  LTC: "#345D9D",
  ATOM: "#2E3148",
};

function colorFor(asset: string): string {
  return COIN_COLORS[asset] ?? "#64748B";
}

function fmtUSD(n: number, digits = 0): string {
  return n.toLocaleString("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

interface DonutSegment {
  asset: string;
  pct: number;
  start: number;
  color: string;
}

function buildDonut(holdings: PortfolioHolding[], total: number): DonutSegment[] {
  if (total <= 0) return [];
  let cumulative = 0;
  return holdings
    .map((h) => {
      const pct = (h.value / total) * 100;
      const seg: DonutSegment = {
        asset: h.asset,
        pct,
        start: cumulative,
        color: colorFor(h.asset),
      };
      cumulative += pct;
      return seg;
    })
    .filter((s) => s.pct > 0);
}

interface StatCardProps {
  label: string;
  value: string;
  sub?: string;
  tone?: "up" | "down" | "neutral";
  big?: boolean;
}

function StatCard({ label, value, sub, tone = "neutral", big = false }: StatCardProps) {
  const valueStyle: CSSProperties = {
    fontSize: big ? 22 : 18,
    fontWeight: 600,
    marginTop: 4,
    letterSpacing: "-0.01em",
    color:
      tone === "up"
        ? "var(--buy)"
        : tone === "down"
          ? "var(--sell)"
          : "var(--text)",
  };
  return (
    <div className="se-card" style={{ padding: 14 }}>
      <div
        className="muted"
        style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}
      >
        {label}
      </div>
      <div className="mono tnum" style={valueStyle}>
        {value}
      </div>
      {sub ? (
        <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
          {sub}
        </div>
      ) : null}
    </div>
  );
}

export function PortfolioPage() {
  const q = useQuery({
    queryKey: ["portfolio"],
    queryFn: fetchPortfolio,
    refetchInterval: 30_000,
  });

  const data = q.data;
  const holdings = data?.holdings ?? [];
  const totalValue = data?.totals.value ?? 0;
  const totalCost = data?.totals.cost ?? 0;
  const unrealizedPnl = data?.totals.unrealizedPnl ?? 0;
  const unrealizedPct = data?.totals.unrealizedPnlPct ?? 0;
  const exchanges = new Set(holdings.map((h) => h.exchange)).size;
  const donut = buildDonut(holdings, totalValue);

  if (q.isLoading) {
    return (
      <div style={{ padding: 32 }}>
        <div className="muted" style={{ fontSize: 12 }}>Loading portfolio…</div>
      </div>
    );
  }

  if (q.isError) {
    return (
      <div style={{ padding: 32 }}>
        <div className="se-card" style={{ padding: 18 }}>
          <div className="down" style={{ fontWeight: 600, fontSize: 13 }}>
            Failed to load portfolio.
          </div>
          <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
            {(q.error as Error)?.message}
          </div>
        </div>
      </div>
    );
  }

  if (holdings.length === 0) {
    return (
      <div style={{ padding: 32 }}>
        <div className="se-card" style={{ padding: 28, textAlign: "center" }}>
          <div
            style={{
              width: 48,
              height: 48,
              margin: "0 auto 14px",
              borderRadius: 12,
              background: "var(--surface-2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--ai)",
            }}
          >
            <Icon name="pie" size={22} />
          </div>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>
            No holdings yet
          </div>
          <div className="muted" style={{ fontSize: 12 }}>
            Connect an exchange in Onboarding or add an API key under Exchange Keys.
            Holdings sync automatically and update on every paper / live trade fill.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 18 }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <StatCard
          label="Total value"
          value={`$${fmtUSD(totalValue)}`}
          sub={`across ${exchanges} exchange${exchanges === 1 ? "" : "s"}`}
          big
        />
        <StatCard
          label="Cost basis"
          value={`$${fmtUSD(totalCost)}`}
          sub={`${holdings.length} asset${holdings.length === 1 ? "" : "s"}`}
        />
        <StatCard
          label="Unrealized P&L"
          value={`${unrealizedPnl >= 0 ? "+" : ""}$${fmtUSD(Math.abs(unrealizedPnl))}`}
          sub={`${unrealizedPct >= 0 ? "+" : ""}${unrealizedPct.toFixed(2)}%`}
          tone={unrealizedPnl >= 0 ? "up" : "down"}
        />
        <StatCard
          label="Largest position"
          value={
            donut.length > 0
              ? `${donut[0]!.asset} · ${donut[0]!.pct.toFixed(1)}%`
              : "—"
          }
          sub={
            donut.length > 0
              ? `$${fmtUSD(holdings[0]!.value)}`
              : "no allocations"
          }
        />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 360px",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <div className="se-card">
          <div className="se-card-head">
            <div>
              Holdings
              <span className="se-card-sub" style={{ marginLeft: 8 }}>
                · {holdings.length} asset{holdings.length === 1 ? "" : "s"}
              </span>
            </div>
          </div>
          <table className="se-table">
            <thead>
              <tr>
                <th>Asset</th>
                <th>Quantity</th>
                <th>Avg cost</th>
                <th>Last price</th>
                <th>Value</th>
                <th>P&L</th>
                <th>Allocation</th>
                <th>Exchange</th>
              </tr>
            </thead>
            <tbody>
              {holdings.map((h) => {
                const allocPct = totalValue > 0 ? (h.value / totalValue) * 100 : 0;
                const up = h.unrealizedPnl >= 0;
                return (
                  <tr key={`${h.exchange}|${h.asset}`}>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <Coin sym={h.asset} size="sm" />
                        <div>
                          <div style={{ fontWeight: 500 }}>{h.asset}</div>
                          <div className="muted" style={{ fontSize: 10 }}>
                            {h.quoteCurrency}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="num">
                      {h.quantity.toLocaleString("en", { maximumFractionDigits: 6 })}
                    </td>
                    <td className="num muted">
                      ${h.avgCostBasis.toLocaleString("en", { maximumFractionDigits: 2 })}
                    </td>
                    <td className="num">
                      ${h.lastPrice.toLocaleString("en", { maximumFractionDigits: 2 })}
                    </td>
                    <td className="num" style={{ fontWeight: 500 }}>
                      ${fmtUSD(h.value)}
                    </td>
                    <td>
                      <div className={`num ${up ? "up" : "down"}`} style={{ fontWeight: 500 }}>
                        {up ? "+" : ""}${fmtUSD(Math.abs(h.unrealizedPnl))}
                      </div>
                      <div
                        className={`num ${up ? "up" : "down"}`}
                        style={{ fontSize: 10, opacity: 0.85 }}
                      >
                        {up ? "+" : ""}
                        {h.unrealizedPnlPct.toFixed(2)}%
                      </div>
                    </td>
                    <td style={{ width: 150 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div className="se-progress" style={{ flex: 1 }}>
                          <div
                            className="se-progress-bar"
                            style={{
                              width: `${Math.min(allocPct, 100)}%`,
                              background: colorFor(h.asset),
                            }}
                          />
                        </div>
                        <span
                          className="num muted"
                          style={{ fontSize: 10, width: 36, textAlign: "right" }}
                        >
                          {allocPct.toFixed(1)}%
                        </span>
                      </div>
                    </td>
                    <td className="muted" style={{ fontSize: 11 }}>
                      {h.exchange}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="se-card">
          <div className="se-card-head">
            <div>Allocation</div>
            <span className="se-pill se-pill-ai">
              <Icon name="sparkle" size={10} /> AI rebalance
            </span>
          </div>
          <div style={{ padding: 16, display: "flex", gap: 14, alignItems: "center" }}>
            <svg width="120" height="120" viewBox="0 0 120 120">
              {donut.map((d) => {
                const r = 48;
                const cx = 60;
                const cy = 60;
                const a1 = (d.start / 100) * Math.PI * 2 - Math.PI / 2;
                const a2 = ((d.start + d.pct) / 100) * Math.PI * 2 - Math.PI / 2;
                const x1 = cx + r * Math.cos(a1);
                const y1 = cy + r * Math.sin(a1);
                const x2 = cx + r * Math.cos(a2);
                const y2 = cy + r * Math.sin(a2);
                const large = d.pct > 50 ? 1 : 0;
                return (
                  <path
                    key={d.asset}
                    d={`M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`}
                    fill={d.color}
                    stroke="var(--surface)"
                    strokeWidth="2"
                  />
                );
              })}
              <circle cx="60" cy="60" r="32" fill="var(--surface)" />
              <text
                x="60"
                y="56"
                textAnchor="middle"
                fill="var(--text-muted)"
                fontSize="9"
              >
                {holdings.length} asset{holdings.length === 1 ? "" : "s"}
              </text>
              <text
                x="60"
                y="70"
                textAnchor="middle"
                fill="var(--text)"
                fontSize="13"
                fontWeight="600"
                fontFamily="JetBrains Mono"
              >
                ${fmtUSD(totalValue)}
              </text>
            </svg>
            <div style={{ flex: 1, fontSize: 11 }}>
              {donut.slice(0, 6).map((d) => (
                <div
                  key={d.asset}
                  style={{ display: "flex", alignItems: "center", gap: 6, padding: "3px 0" }}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 2,
                      background: d.color,
                    }}
                  />
                  <span style={{ flex: 1 }}>{d.asset}</span>
                  <span className="mono tnum">{d.pct.toFixed(1)}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
